import BackendProviderWrapper from "./backend-provider-wrapper";

// Import any other providers here

/**
 * ProviderFactory - Factory class for creating and managing AI providers
 * Now uses backend providers exclusively via BackendProviderWrapper
 */
class ProviderFactory {
  constructor() {
    this.providers = {};

    // Always use backend providers
    this.useBackend = true;

    this.availableProviders = {
      openai: {
        name: "OpenAI",
        class: null, // Will use backend wrapper
        baseUrl: "https://api.openai.com/v1",
        defaultModel: "gpt-4.1-mini",
        requiresApiKey: false, // Backend handles API keys
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsFileUpload: true,
        supportsVision: true,
        supportsFileRetrieval: true,
        visionModels: ["gpt-4-vision-preview", "gpt-4o", "gpt-4o-mini"],
      },
      gemini: {
        name: "Google Gemini",
        class: null, // Will use backend wrapper
        baseUrl: "https://generativelanguage.googleapis.com/v1beta/openai",
        defaultModel: "gemini-2.5-flash",
        requiresApiKey: false, // Backend handles API keys
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsFileUpload: true,
        supportsVision: true,
        supportsFileRetrieval: false,
        visionModels: [
          "gemini-2.5-pro",
          "gemini-2.5-flash",
          "gemini-2.5-flash-lite",
          "gemini-3-pro-preview",
        ],
      },
      mistral: {
        name: "Mistral AI",
        class: null, // Will use backend wrapper
        baseUrl: "https://api.mistral.ai/v1",
        defaultModel: "mistral-large-latest",
        requiresApiKey: false, // Backend handles API keys
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsFileUpload: false,
        supportsVision: false,
        supportsFileRetrieval: false,
        visionModels: [],
      },
      deepseek: {
        name: "DeepSeek",
        class: null, // Will use backend wrapper
        baseUrl: "https://api.deepseek.com/v1",
        defaultModel: "deepseek-chat",
        requiresApiKey: false, // Backend handles API keys
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsFileUpload: false,
        supportsVision: false,
        supportsFileRetrieval: false,
        visionModels: [],
      },
      xai: {
        name: "X AI",
        class: null, // Will use backend wrapper
        baseUrl: "https://api.x.ai/v1",
        defaultModel: "grok-3",
        requiresApiKey: false, // Backend handles API keys
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsFileUpload: false,
        supportsVision: true,
        supportsFileRetrieval: false,
        visionModels: ["grok-2-vision", "grok-2-vision-latest"],
      },
      anthropic: {
        name: "Anthropic",
        class: null, // Will use backend wrapper
        baseUrl: "https://api.anthropic.com",
        defaultModel: "claude-3-5-haiku-20241022",
        requiresApiKey: false, // Backend handles API keys
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsFileUpload: true,
        supportsVision: true,
        supportsFileRetrieval: false,
        visionModels: [
          "claude-opus-4-20250514",
          "claude-sonnet-4-20250514",
          "claude-sonnet-4-5-20250929",
          "claude-3-7-sonnet-20250219",
          "claude-3-5-haiku-20241022",
        ],
      },
      groq: {
        name: "Groq",
        class: null, // Will use backend wrapper - routes to backend GroqProvider which uses Groq API
        baseUrl: "https://api.groq.com/openai/v1", // Groq's OpenAI-compatible API endpoint (not used directly, backend handles routing)
        defaultModel: "llama-3.3-70b-versatile",
        requiresApiKey: false, // Backend handles API keys and routes to GroqProvider
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsFileUpload: false,
        supportsVision: false,
        supportsFileRetrieval: false,
        visionModels: [],
      },
      cohere: {
        name: "Cohere",
        class: null, // Will use backend wrapper
        baseUrl: "https://api.cohere.ai/compatibility/v1",
        defaultModel: "command-a-03-2025",
        requiresApiKey: false, // Backend handles API keys
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsFileUpload: false,
        supportsVision: false,
        supportsFileRetrieval: false,
        visionModels: [],
      },
      perplexity: {
        name: "Perplexity",
        class: null, // Will use backend wrapper
        baseUrl: "https://api.perplexity.ai",
        defaultModel: "sonar",
        requiresApiKey: false, // Backend handles API keys
        supportsStreaming: true,
        supportsFunctionCalling: false,
        supportsFileUpload: false,
        supportsVision: false,
        supportsFileRetrieval: false,
        visionModels: [],
      },
      openrouter: {
        name: "OpenRouter",
        class: null, // Will use backend wrapper
        baseUrl: "https://openrouter.ai/api/v1",
        defaultModel: "openai/o1-mini",
        requiresApiKey: false, // Backend handles API keys
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsFileUpload: false,
        supportsVision: false,
        supportsFileRetrieval: false,
        visionModels: [],
      },
      qwen: {
        name: "Qwen",
        class: null, // Will use backend wrapper
        baseUrl: "https://dashscope-intl.aliyuncs.com/compatible-mode/v1",
        defaultModel: "qwen-max-latest",
        requiresApiKey: false, // Backend handles API keys
        supportsStreaming: true,
        supportsFunctionCalling: true,
        supportsFileUpload: false,
        supportsVision: false,
        supportsFileRetrieval: false,
        visionModels: [],
      },
      // Add more providers here as they are implemented
    };
  }

  /**
   * Get an instance of a provider (Backend Only)
   * @param {string} providerName - Name of the provider to get
   * @param {Object} options - Provider options
   * @returns {BackendProviderWrapper} Backend provider wrapper instance
   */
  getProvider(providerName, options = {}) {
    const providerKey = providerName.toLowerCase();

    // Check if provider exists in factory
    if (!this.availableProviders[providerKey]) {
      console.error(
        "🏭 [PROVIDER-FACTORY] Provider not supported:",
        providerKey
      );
      throw new Error(`Provider '${providerName}' is not supported`);
    }

    // Get provider config
    const providerConfig = this.availableProviders[providerKey];

    // Always return a backend wrapper
    return new BackendProviderWrapper(providerKey, {
      ...options,
      defaultModel: options.defaultModel || providerConfig.defaultModel,
    });
  }

  /**
   * Set whether to use backend providers (always true now)
   * @param {boolean} useBackend - Whether to use backend providers
   */
  setUseBackend(useBackend) {
    // Always use backend, but keep method for compatibility
    this.useBackend = true;
  }

  /**
   * Check if currently using backend providers (always true now)
   * @returns {boolean} - Whether using backend providers
   */
  isUsingBackend() {
    return true;
  }

  /**
   * Get a list of all available providers
   * @returns {Array} List of provider configurations
   */
  getAllProviders() {
    return Object.entries(this.availableProviders).map(([key, config]) => ({
      id: key,
      name: config.name,
      defaultModel: config.defaultModel,
      requiresApiKey: config.requiresApiKey,
      hasEnvApiKey: false, // Backend handles API keys
      supportsStreaming: config.supportsStreaming || false,
      supportsFunctionCalling: config.supportsFunctionCalling || false,
      supportsFileUpload: config.supportsFileUpload || false,
      supportsVision: config.supportsVision || false,
      supportsFileRetrieval: config.supportsFileRetrieval || false,
      visionModels: config.visionModels || [],
      usingBackend: true,
    }));
  }

  /**
   * Get models from a provider that support vision capabilities
   * @param {string} providerName - Name of the provider
   * @returns {Array} List of vision-capable model IDs
   */
  getVisionModels(providerName) {
    const providerKey = providerName.toLowerCase();
    const providerConfig = this.availableProviders[providerKey];

    if (!providerConfig) {
      throw new Error(`Provider '${providerName}' is not supported`);
    }

    return providerConfig.visionModels || [];
  }

  /**
   * Check if a specific model supports vision capabilities
   * @param {string} providerName - Name of the provider
   * @param {string} modelId - Model ID to check
   * @returns {boolean} Whether the model supports vision
   */
  supportsVision(providerName, modelId) {
    const visionModels = this.getVisionModels(providerName);
    return visionModels.includes(modelId);
  }

  /**
   * Add a custom provider to the factory
   * @param {string} key - Provider key
   * @param {Object} config - Provider configuration
   */
  registerProvider(key, config) {
    if (!config.name) {
      throw new Error("Provider must have a name");
    }

    this.availableProviders[key.toLowerCase()] = {
      name: config.name,
      class: null, // Always use backend wrapper
      baseUrl: config.baseUrl,
      defaultModel: config.defaultModel,
      requiresApiKey: config.requiresApiKey !== false,
      supportsStreaming: config.supportsStreaming || false,
      supportsFunctionCalling: config.supportsFunctionCalling || false,
      supportsFileUpload: config.supportsFileUpload || false,
      supportsVision: config.supportsVision || false,
      supportsFileRetrieval: config.supportsFileRetrieval || false,
      visionModels: config.visionModels || [],
    };

    // Clear cached instance if it exists
    if (this.providers[key.toLowerCase()]) {
      delete this.providers[key.toLowerCase()];
    }
  }
}

// Create singleton instance
const providerFactory = new ProviderFactory();

export default providerFactory;
